import { BaseModel } from '../base/base-model'
import { ROLE } from '../base/role'
import { TRANSITION_NAMES } from './transition'

/**
 * @memberOf loavies.models.transaction
 * @constructor
 */
export class TransactionModel extends BaseModel {
  /**
   * @param {string} id
   * @param {Date} createdAt
   * @param {loavies.models.transaction.HistoricalTransitionModel} lastHistoricalTransition
   * @param {Date} lastTransitionedAt
   * @param {loavies.models.transaction.LineItemModel[]} lineItems
   * @param {loavies.models.transaction.TransactionMetaDataModel} metaData
   * @param {loavies.models.base.PriceModel} payinTotal
   * @param {loavies.models.base.PriceModel} payoutTotal
   * @param {loavies.models.transaction.TransactionProtectedDataModel} protectedData
   * @param {loavies.models.transaction.HistoricalTransitionModel[]} transitionHistory
   * @param {string} [userRole]
   * @param {loavies.models.transaction.TransitionModel[]} nextTransitions
   * @param {loavies.models.listing.ListingModel} [listing]
   * @param {loavies.models.user.UserModel} customer
   * @param {loavies.models.user.UserModel} provider
   * @param {loavies.models.review.ReviewModel[]} reviews
   */
  constructor({
    id,
    createdAt,
    lastHistoricalTransition,
    lastTransitionedAt,
    lineItems,
    metaData,
    payinTotal,
    payoutTotal,
    protectedData,
    transitionHistory,
    userRole,
    nextTransitions,
    listing,
    customer,
    provider,
    reviews,
  }) {
    super()

    /** @type {string} */
    this.id = id

    /** @type {Date} */
    this.createdAt = createdAt

    /** @type {loavies.models.transaction.HistoricalTransitionModel} */
    this.lastHistoricalTransition = lastHistoricalTransition

    /** @type {Date} */
    this.lastTransitionedAt = lastTransitionedAt

    /** @type {loavies.models.transaction.LineItemModel[]} */
    this.lineItems = lineItems

    /** @type {loavies.models.transaction.TransactionMetaDataModel} */
    this.metaData = metaData

    /** @type {loavies.models.base.PriceModel} */
    this.payinTotal = payinTotal

    /** @type {loavies.models.base.PriceModel} */
    this.payoutTotal = payoutTotal

    /** @type {loavies.models.transaction.TransactionProtectedDataModel} */
    this.protectedData = protectedData

    /** @type {loavies.models.transaction.HistoricalTransitionModel[]} */
    this.transitionHistory = transitionHistory

    /** @type {null|string} */
    this.userRole = userRole ?? null

    /** @type {Array<loavies.models.transaction.TransitionModel>} */
    this.nextTransitions = nextTransitions

    /** @type {null|loavies.models.listing.ListingModel} */
    this.listing = listing ?? null

    /** @type {loavies.models.user.UserModel} */
    this.customer = customer

    /** @type {loavies.models.user.UserModel} */
    this.provider = provider

    /** @type {string} */
    this.state = this.getState(lastHistoricalTransition.name)

    /** @type {loavies.models.review.ReviewModel[]} */
    this.reviews = reviews
  }

  /**
   * @param {string} lastTransitionName
   * @return {string}
   */
  getState(lastTransitionName) {
    return MAPPED_TRANSITION_NAMES_TO_TRANSACTION_STATES[lastTransitionName]
  }
}

// States which transaction can be transitioned to
export const TRANSACTION_STATES = {
  CANCELED: 'canceled',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_COMPLETE: 'payment-complete',
  ITEM_SENT: 'item-sent',
  DELIVERED: 'delivered',
  NOT_DELIVERED: 'not-delivered',
  COMPLETED: 'completed',
  COMPLETED_AND_REVIEWED: 'completed-and-reviewed',
  DISPUTE: 'dispute',
  REFUNDED: 'refunded',
}

// The last transition of a transaction determines
// the state of a transaction.
export const MAPPED_TRANSITION_NAMES_TO_TRANSACTION_STATES = {
  [TRANSITION_NAMES.ENQUIRE]: TRANSACTION_STATES.PENDING_PAYMENT,
  [TRANSITION_NAMES.FAILED_PAYMENT]: TRANSACTION_STATES.CANCELED,
  [TRANSITION_NAMES.CONFIRM_PAYMENT]: TRANSACTION_STATES.PAYMENT_COMPLETE,
  [TRANSITION_NAMES.CANCEL_PURCHASE]: TRANSACTION_STATES.CANCELED,
  [TRANSITION_NAMES.CANCEL_PURCHASE_BY_OPERATOR ]: TRANSACTION_STATES.CANCELED,
  [TRANSITION_NAMES.CANCEL_PAYMENT_BY_OPERATOR ]: TRANSACTION_STATES.CANCELED,
  [TRANSITION_NAMES.SEND_ITEM]: TRANSACTION_STATES.ITEM_SENT,
  [TRANSITION_NAMES.DELIVERED]: TRANSACTION_STATES.DELIVERED,
  [TRANSITION_NAMES.ITEM_NOT_SENT]: TRANSACTION_STATES.NOT_DELIVERED,
  [TRANSITION_NAMES.NOT_DELIVERED]: TRANSACTION_STATES.NOT_DELIVERED,
  [TRANSITION_NAMES.REVIEW_BY_CUSTOMER]: TRANSACTION_STATES.COMPLETED_AND_REVIEWED,
  [TRANSITION_NAMES.COMPLETE_BY_OPERATOR]: TRANSACTION_STATES.COMPLETED,
  [TRANSITION_NAMES.COMPLETE_BY_CUSTOMER]: TRANSACTION_STATES.COMPLETED,
  [TRANSITION_NAMES.DISPUTE_RESOLVED_BY_OPERATOR]: TRANSACTION_STATES.COMPLETED,
  [TRANSITION_NAMES.DISPUTE_RESOLVED_BY_CUSTOMER]: TRANSACTION_STATES.COMPLETED,
  [TRANSITION_NAMES.DISPUTE_BY_CUSTOMER]: TRANSACTION_STATES.DISPUTE,
  [TRANSITION_NAMES.REFUND_NOT_DELIVERED]: TRANSACTION_STATES.REFUNDED,
  [TRANSITION_NAMES.REFUND_AFTER_DISPUTE]: TRANSACTION_STATES.REFUNDED,
  [TRANSITION_NAMES.REFUND_AFTER_DISPUTE_BY_OPERATOR]: TRANSACTION_STATES.REFUNDED,
}

// A listing can still be active when a transaction is linked to it.
// For example a transaction can be canceled etc.
export const ACTIVE_LISTING_ELIGIBLE_TRANSACTION_STATES_CUSTOMER = [
  TRANSACTION_STATES.CANCELED,
]

export const ACTIVE_LISTING_ELIGIBLE_TRANSACTION_STATES_PROVIDER = [
  TRANSACTION_STATES.CANCELED,
  TRANSACTION_STATES.PENDING_PAYMENT,
]

// When in one of these states, the user can perform an action as provider or customer
export const USER_ACTIONABLE_TRANSACTION_STATES = {
  [ROLE.CUSTOMER]: [
    TRANSACTION_STATES.DELIVERED,
    TRANSACTION_STATES.DISPUTE,
    TRANSACTION_STATES.COMPLETED,
  ],
  [ROLE.PROVIDER]: [
    TRANSACTION_STATES.PAYMENT_COMPLETE,
    TRANSACTION_STATES.DISPUTE,
  ],
}

export const USER_VISIBLE_TRANSACTION_STATES_PROVIDER = Object.values(TRANSACTION_STATES)
  .filter(transactionState => !ACTIVE_LISTING_ELIGIBLE_TRANSACTION_STATES_PROVIDER.includes(transactionState))

export const USER_VISIBLE_TRANSACTION_STATES_CUSTOMER = Object.values(TRANSACTION_STATES)
  .filter(transactionState => !ACTIVE_LISTING_ELIGIBLE_TRANSACTION_STATES_CUSTOMER.includes(transactionState))
